import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { useLocation, useNavigate } from 'react-router-dom';
import "../Tour/shepherd-theme.css";


const TourContext = createContext({
  startTour: () => { },
  stopTour: () => { },
  resetTour: () => { },
  running: false,
  canResume: false,
});

export const useTour = () => useContext(TourContext);

const STEPS = [
  {
    id: 'library',
    route: '/library',
    target: 'div[data-bs-target="#Library-Management"]',
    title: '📂 Media Library',
    text: 'Here’s where all your uploaded content lives. Browse, search, and manage files in one place.',
    placement: 'right'
  },
  {
    id: 'video',
    route: '/video-lists',
    target: '#videoManagement',
    title: '🎬 Video Management',
    text: 'Access all video tools here. Review statuses, edit details, and publish new content effortlessly.',
    placement: 'right'
  },
  {
    id: 'series',
    route: '/series-list',
    target: 'div[data-bs-target="#Series-Episode"]',
    title: '📺 Series & Episodes',
    text: 'Organize your shows by creating series and managing their episodes in one structured space.',
    placement: 'right'
  },
  {
    id: 'audio',
    route: '/audio-lists',
    target: 'div[data-bs-target="#Audio-Management"]',
    title: '🎧 Audio Management',
    text: 'Upload and manage podcasts or audio tracks. Perfect for building an audio library for your users.',
    placement: 'right'
  },
  {
    id: 'live',
    route: '/livestream',
    target: 'div[data-bs-target="#Live-Stream"]',
    title: '🔴 Live Streaming',
    text: 'Go live with real-time streaming. Create and manage livestream events from this section.',
    placement: 'right'
  },
  {
    id: 'users',
    route: '/users',
    target: 'div[data-bs-target="#User"]',
    title: '👥 User Management',
    text: 'Add new users, set permissions, or manage user limits with powerful tools designed for scale.',
    placement: 'right'
  },
  {
    id: 'plans',
    route: '/subscription-plans',
    target: 'div[data-bs-target="#Plans"]',
    title: '💳 Subscription Plans',
    text: 'Create and customize subscription tiers or in-app plans to monetize your platform.',
    placement: 'right'
  },
  {
    id: 'payments',
    route: '/payment_settings',
    target: 'div[data-bs-target="#Payment-Management"]',
    title: '💵 Payment Settings',
    text: 'Configure gateways like Stripe or PayPal. Securely handle all payment-related settings here.',
    placement: 'right'
  },
  {
    id: 'storefront',
    route: '/settings',
    target: 'a[href="/settings"]',
    title: '🏪 Storefront Settings',
    text: 'Control your storefront look and feel. Adjust RTMP, branding, and general site settings.',
    placement: 'right'
  },
];


export const TourProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [running, setRunning] = useState(false);
  const [canResume] = useState(false); // always false per requirement
  const currentIndex = useRef(0);
  const tourRef = useRef(null);
  const cancelledByNav = useRef(false);
  const closingRef = useRef(false);

  const waitForTarget = useCallback(async (selector, timeout = 5000) => {
    const start = performance.now();
    return await new Promise((resolve) => {
      const loop = () => {
        const el = document.querySelector(selector);
        if (el) return resolve(true);
        if (performance.now() - start > timeout) return resolve(false);
        requestAnimationFrame(loop);
      };
      loop();
    });
  }, []);

  const disposeTour = () => {
    if (tourRef.current) {
      try {
        // Prevent cancel handler from recursively calling finish
        closingRef.current = true;
        tourRef.current.cancel();
      } catch { }
      tourRef.current = null;
      closingRef.current = false;
    }
  };

  const finish = useCallback(() => {
    disposeTour();
    setRunning(false);
    currentIndex.current = 0;
  }, []);

  const makeStepButtons = (idx) => {
    const buttons = [];
    if (idx > 0) {
      buttons.push({
        text: 'Back',
        action: () => {
          cancelledByNav.current = true;
          disposeTour();
          showStep(idx - 1);
        },
        secondary: true,
      });
    }
    buttons.push({
      text: idx === STEPS.length - 1 ? 'Finish' : 'Next',
      action: () => {
        if (idx === STEPS.length - 1) {
          finish();
        } else {
          cancelledByNav.current = true;
          disposeTour();
          showStep(idx + 1);
        }
      },
    });
    buttons.push({
      text: 'Skip',
      action: () => finish(),
      secondary: true,
    });
    return buttons;
  };

  const showStep = useCallback(async (idx) => {
    currentIndex.current = idx;
    const step = STEPS[idx];
    if (!step) return finish();

    if (location.pathname !== step.route) {
      navigate(step.route);
    }

    const ok = await waitForTarget(step.target, 8000);
    if (!ok) {
      // If target not found, move on to next to avoid getting stuck
      if (idx < STEPS.length - 1) return showStep(idx + 1);
      return finish();
    }

    const tour = new Shepherd.Tour({
      defaultStepOptions: {
        classes: 'shadow-lg bg-white rounded-xl',
        scrollTo: {
          behavior: 'smooth',
          block: 'center' // 👈 this makes sure target is centered
        }
      },
      useModalOverlay: true, // optional
    });


    tour.addStep({
      id: step.id,
      title: step.title,
      text: step.text,
      attachTo: { element: step.target, on: step.placement || 'bottom' },
      buttons: makeStepButtons(idx),
    });

    tour.on('cancel', () => {
      // If we cancelled programmatically (navigation/finish), do nothing
      if (closingRef.current) {
        cancelledByNav.current = false;
        return;
      }
      if (!cancelledByNav.current) finish();
      cancelledByNav.current = false;
    });

    tourRef.current = tour;
    tour.start();
  }, [finish, navigate, location.pathname, waitForTarget]);

  const startTour = useCallback(() => {
    disposeTour();
    setRunning(true);
    showStep(0);
  }, [showStep]);

  const stopTour = useCallback(() => {
    finish();
  }, [finish]);

  const resetTour = useCallback(() => {
    currentIndex.current = 0;
    disposeTour();
    setRunning(false);
  }, []);

  useEffect(() => () => disposeTour(), []);

  const value = useMemo(() => ({ startTour, stopTour, resetTour, running, canResume }), [startTour, stopTour, resetTour, running, canResume]);

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export default TourProvider;
